import React, { type ReactNode } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import type { TriggerMode } from '@atlaskit/teams-app-internal-popup-adaptor/popup-trigger-with-hover';

const styles = cssMap({
	trigger: {
		all: 'unset',
		display: 'inline',
	},
	// Opt-in full-width trigger so the element can host a full-width row (e.g. a menu item) without
	// collapsing to its content width. Defaults off to preserve the inline trigger behaviour.
	fullWidthTrigger: {
		all: 'unset',
		display: 'block',
		width: '100%',
	},
});

const messages = defineMessages({
	fallbackAriaLabel: {
		id: 'user-profile-card.UserProfileCardTrigger.fallbackAriaLabel',
		defaultMessage: 'More information about this user',
		description:
			'Accessible label announced by screen readers for the profile card trigger when no explicit label is supplied.',
	},
});

type UserProfileCardTriggerProps = {
	/** Resolved by {@link UserProfileCardWithTrigger} (defaults to `'click'` when that prop is omitted). */
	trigger: TriggerMode;
	triggerProps: TriggerProps;
	children: ReactNode;
	/** When `true`, hides the trigger from assistive tech (`aria-hidden` + `tabIndex={-1}`). */
	ariaHideProfileTrigger?: boolean;
	/**
	 * Accessible name for the trigger element. When omitted, a generic fallback
	 * ("More information about this user") is used. Mirrors the `ariaLabel` prop on legacy
	 * `@atlaskit/profilecard`. Ignored when {@link ariaHideProfileTrigger} is `true`.
	 */
	ariaLabel?: string;
	/**
	 * When `true`, the trigger element fills the available width (`display: block; width: 100%`)
	 * instead of collapsing to its content width. Use when the trigger wraps a full-width element
	 * such as a menu row. Defaults to `false` (inline trigger).
	 */
	fullWidthTrigger?: boolean;
};

export const UserProfileCardTrigger = ({
	trigger,
	triggerProps,
	children,
	ariaHideProfileTrigger,
	ariaLabel,
	fullWidthTrigger = false,
}: UserProfileCardTriggerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const hoverOnlyTrigger = Array.isArray(trigger)
		? trigger.every((mode) => mode === 'hover')
		: trigger === 'hover';
	const useNonInteractiveHoverTrigger = hoverOnlyTrigger;

	const accessibleName = ariaHideProfileTrigger
		? undefined
		: (ariaLabel ?? formatMessage(messages.fallbackAriaLabel));

	if (useNonInteractiveHoverTrigger) {
		const { 'aria-expanded': _ariaExpanded, ...nonDialogTriggerProps } = triggerProps;

		if (fg('teams_a11y_user_profilecard')) {
			return (
				<Box
					as="span"
					{...nonDialogTriggerProps}
					testId="user-profile-card-trigger-wrapper"
					xcss={styles.trigger}
					aria-hidden={ariaHideProfileTrigger ? true : undefined}
				>
					{children}
				</Box>
			);
		}

		return (
			<Box
				as="span"
				{...nonDialogTriggerProps}
				testId="user-profile-card-trigger-wrapper"
				xcss={fullWidthTrigger ? styles.fullWidthTrigger : styles.trigger}
				role={ariaHideProfileTrigger ? undefined : 'button'}
				aria-label={accessibleName}
				aria-hidden={ariaHideProfileTrigger ? true : undefined}
				tabIndex={ariaHideProfileTrigger ? -1 : 0}
			>
				{children}
			</Box>
		);
	}

	return (
		<Pressable
			{...triggerProps}
			testId="user-profile-card-trigger-wrapper"
			xcss={fullWidthTrigger ? styles.fullWidthTrigger : styles.trigger}
			aria-label={accessibleName}
			aria-hidden={ariaHideProfileTrigger ? true : undefined}
			tabIndex={ariaHideProfileTrigger ? -1 : undefined}
		>
			{children}
		</Pressable>
	);
};
